import React from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Select, 
  Card, 
  Space, 
  Tag, 
  Typography, 
  Row, 
  Col,
  Alert,
  Spin,
  Tooltip,
  Popconfirm,
  Avatar,
  Statistic
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  FilterOutlined,
  DownloadOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CrownOutlined,
  SettingOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import { useTranslation } from '../../hooks/useTranslation';
import { PermissionGuard } from '../PermissionGuard';
import { AdminPermission as GraphQLAdminPermission } from '../../generated/graphql';
import { AdminUser, convertRoleValueToKey } from './types';

const { Text } = Typography;
const { Option } = Select;
const { Search } = Input;

interface AdminUsersTabProps {
  adminUsers: AdminUser[];
  filteredAdminUsers: AdminUser[];
  loadingAdminUsers: boolean;
  adminUsersError: any;
  refetch: () => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  roleFilter: string;
  setRoleFilter: (filter: string) => void;
  statusFilter: string;
  setStatusFilter: (filter: string) => void;
  showCreateModal: boolean;
  setShowCreateModal: (show: boolean) => void;
  editingAdmin: AdminUser | null;
  setEditingAdmin: (admin: AdminUser | null) => void;
  editingAdminPermissions: AdminUser | null;
  setEditingAdminPermissions: (admin: AdminUser | null) => void;
  editingAdminRole: AdminUser | null;
  setEditingAdminRole: (admin: AdminUser | null) => void;
  isSubmitting: boolean;
  form: any;
  permissionsForm: any;
  roleForm: any;
  doctors: any[];
  loadingDoctors: boolean;
  availableRoles: any[];
  handleCreateAdmin: (values: any) => void;
  handleUpdateAdmin: (id: string, values: any) => void;
  handleUpdateAdminPermissions: (id: string, values: any) => void;
  handleUpdateAdminRole: (id: string, values: any) => void;
  handleDeleteAdmin: (id: string) => void;
  handleToggleStatus: (id: string, isActive: boolean) => void;
  getRoleTag: (role: string) => JSX.Element;
  getStatusTag: (isActive: boolean) => JSX.Element;
  columns: any[];
  activeAdmins: number;
  inactiveAdmins: number;
  superAdmins: number;
}

const AdminUsersTab: React.FC<AdminUsersTabProps> = ({
  adminUsers,
  filteredAdminUsers,
  loadingAdminUsers,
  adminUsersError,
  refetch,
  searchTerm,
  setSearchTerm,
  roleFilter,
  setRoleFilter,
  statusFilter,
  setStatusFilter,
  showCreateModal,
  setShowCreateModal,
  editingAdmin,
  setEditingAdmin,
  editingAdminPermissions,
  setEditingAdminPermissions,
  editingAdminRole,
  setEditingAdminRole,
  isSubmitting,
  form,
  permissionsForm,
  roleForm,
  doctors,
  loadingDoctors,
  availableRoles,
  handleCreateAdmin,
  handleUpdateAdmin,
  handleUpdateAdminPermissions,
  handleUpdateAdminRole,
  handleDeleteAdmin,
  handleToggleStatus,
  getRoleTag,
  getStatusTag,
  columns,
  activeAdmins,
  inactiveAdmins,
  superAdmins
}) => {
  const { t } = useTranslation();

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <PermissionGuard permission={GraphQLAdminPermission.CreateUsers}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setShowCreateModal(true);
                form.resetFields();
              }}
            >
              {t('adminUsers.addAdminUser')}
            </Button>
          </PermissionGuard>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('adminUsers.totalAdmins')}
              value={adminUsers.length}
              prefix={<TeamOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('adminUsers.active')}
              value={activeAdmins}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('adminUsers.inactive')}
              value={inactiveAdmins}
              prefix={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('adminUsers.superAdmins')}
              value={superAdmins}
              prefix={<CrownOutlined style={{ color: '#f5222d' }} />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder={t('adminUsers.searchAdminUsers')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder={t('adminUsers.filterByRole')}
              value={roleFilter}
              onChange={setRoleFilter}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="all">{t('adminUsers.allRoles')}</Option>
              {availableRoles.map((role: any) => (
                <Option key={role.id} value={role.id}>
                  {role.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder={t('adminUsers.filterByStatus')}
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="all">{t('adminUsers.allStatus')}</Option>
              <Option value="active">{t('adminUsers.statuses.active')}</Option>
              <Option value="inactive">{t('adminUsers.statuses.inactive')}</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Space>
              <Button icon={<FilterOutlined />}>
                {t('adminUsers.clearFilters')}
              </Button>
              <Button icon={<DownloadOutlined />}>
                {t('common.export')}
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card>
        {loadingAdminUsers ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>{t('common.loading')}</div>
          </div>
        ) : adminUsersError ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Alert
              message={t('common.error')}
              description={t('adminUsers.errorLoadingAdminUsers')}
              type="error"
              showIcon
              action={
                <Button size="small" onClick={() => refetch()}>
                  {t('common.retry')}
                </Button>
              }
            />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={filteredAdminUsers}
            rowKey="id"
            pagination={{
              total: filteredAdminUsers.length,
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => t('adminUsers.paginationInfo', { start: range[0], end: range[1], total }),
            }}
          />
        )}
      </Card>
    </div>
  );
};

export default AdminUsersTab;
